import React from "react";
import { useDispatch } from "react-redux";
import { Box, TextField, Typography, Chip, CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import { addObjectTag, removeObjectTag } from "../../actions/tagActions";

const TagsSection = ({
  tags = [],
  onTagsChange,
  onTagsRemove,
  objectId,
  objectType,
  title = "Tags",
  placeholder = "Press Enter to add",
}) => {
  const dispatch = useDispatch();
  const [tagInput, setTagInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleTagInputChange = e => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = async e => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      if (tagInput?.trim()) {
        const tagValue = tagInput.trim();

        // Check for duplicate
        if (tags.some(tag => tag.tag === tagValue)) {
          setTagInput("");
          return;
        }

        setLoading(true);
        try {
          // Immediately call API to add tag
          await dispatch(
            addObjectTag({
              object_id: objectId,
              object_type: objectType,
              tag: tagValue,
            })
          );

          // Update local state after successful API call
          onTagsChange([...tags, { tag: tagValue, newTag: false }]);
          setTagInput("");
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Failed to add tag",
            text: error.message || "Please try again",
          });
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleTagDelete = async tagToDelete => {
    const tagToRemove = tags.find(t => t.tag === tagToDelete);

    setLoading(true);
    try {
      // Immediately call API to remove tag
      await dispatch(
        removeObjectTag({
          object_id: objectId,
          object_type: objectType,
          tag: tagToDelete,
        })
      );

      // Update local state after successful API call
      const newTags = tags.filter(t => t.tag !== tagToDelete);
      onTagsChange(newTags);
      onTagsRemove?.(tagToRemove);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed to remove tag",
        text: error.message || "Please try again",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Box display="grid" gap={2}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            label="Add Tag"
            value={tagInput}
            onChange={handleTagInputChange}
            onKeyDown={handleTagInputKeyDown}
            placeholder={placeholder}
            size="small"
            disabled={loading}
            helperText="Press Enter to save tag. It will appear as a chip below when saved."
          />
          {loading && <CircularProgress size={24} sx={{ mt: 1 }} />}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {tags.map(tag => (
            <Chip
              key={tag.tag}
              label={tag.tag}
              onDelete={() => handleTagDelete(tag.tag)}
              disabled={loading}
              sx={{
                marginRight: 1,
                marginBottom: 1,
                animation: tag.newTag === false ? "fadeIn 0.3s ease-in" : "none",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "scale(0.8)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TagsSection;
